import type { ArchiveEntryItem } from "../../runtime/archiveTypes";
import type {
	EntriesGridCardStyle,
	EntriesGridColumns,
	EntriesGridCtaStyle,
	EntriesGridDensity,
	EntriesGridElement,
	EntriesGridImageAspect,
	EntriesGridMode,
	EntriesGridResponsiveBucket,
	EntriesGridResponsiveLayout,
	EntriesGridSortBy,
	EntriesGridSortDir,
	EntriesGridVariant,
} from "../types";

export type EntriesGridResolvedItem = ArchiveEntryItem;

export type EntriesGridResolveRequest = {
	columns?: EntriesGridColumns;
	entryIds?: string[];
	excludeCurrentEntry?: boolean;
	limit?: number;
	mode: EntriesGridMode;
	postTypeId?: string;
	sortBy?: EntriesGridSortBy;
	sortDir?: EntriesGridSortDir;
	taxonomyTermId?: string;
	variant?: EntriesGridVariant;
};

export type EntriesGridPickerEntry = {
	id: string;
	slug: string;
	title: string;
};

export const ENTRIES_GRID_LIMIT_DEFAULT = 6;
/** Suggested title clip when truncate is enabled (inspector default). */
export const ENTRIES_GRID_TITLE_MAX_CHARS_DEFAULT = 80;
/** Suggested excerpt clip when truncate is enabled (inspector default). */
export const ENTRIES_GRID_EXCERPT_MAX_CHARS_DEFAULT = 160;
export const ENTRIES_GRID_MAX_CHARS_CAP = 2000;

export function clampEntriesGridMaxChars(
	raw: unknown,
	fallback: number
): number {
	const n =
		typeof raw === "number" ? raw : Number.parseInt(String(raw ?? ""), 10);
	if (!Number.isFinite(n) || n < 1) {
		return fallback;
	}
	return Math.min(Math.floor(n), ENTRIES_GRID_MAX_CHARS_CAP);
}

/**
 * Soft-truncate plain text for card title/excerpt. Returns the original string
 * unchanged when truncate is off or under the limit.
 */
export function truncateEntriesGridText(
	raw: string,
	opts: { enabled: boolean; maxChars: number }
): { text: string; truncated: boolean; full: string } {
	const full = raw;
	if (!opts.enabled || opts.maxChars < 1) {
		return { text: full, truncated: false, full };
	}
	const trimmed = full.trimEnd();
	if (trimmed.length <= opts.maxChars) {
		return { text: full, truncated: false, full };
	}
	const cut = trimmed.slice(0, opts.maxChars).trimEnd();
	return { text: `${cut}…`, truncated: true, full };
}

export const ENTRIES_GRID_EDITOR_PLACEHOLDERS: EntriesGridResolvedItem[] = [
	{
		id: "placeholder-1",
		href: "#",
		title: "Sample published entry",
		excerpt: "Excerpt preview for the entries grid card.",
		imageUrl: null,
		imageWidth: null,
		imageHeight: null,
		imageAlt: "Sample entry",
		publishedAt: "2026-05-25T12:00:00.000Z",
		readTimeMinutes: 5,
		authorName: "Alex Morgan",
		authorInitials: "AM",
		authorImageUrl: null,
		category: {
			slug: "guides",
			label: "Guides",
			accent: "var(--tt-color-primary, #2563eb)",
		},
	},
	{
		id: "placeholder-2",
		href: "#",
		title: "Another sample entry",
		excerpt: "Second card shown while the host resolves live entries.",
		imageUrl: null,
		imageWidth: null,
		imageHeight: null,
		imageAlt: "Sample entry",
		publishedAt: "2026-05-20T12:00:00.000Z",
		readTimeMinutes: 8,
		authorName: "Jordan Lee",
		authorInitials: "JL",
		authorImageUrl: null,
		category: {
			slug: "news",
			label: "News",
			accent: "var(--tt-color-secondary, #0d9488)",
		},
	},
	{
		id: "placeholder-3",
		href: "#",
		title: "Third sample entry",
		excerpt: "Use dynamic or static mode to fill this grid.",
		imageUrl: null,
		imageWidth: null,
		imageHeight: null,
		imageAlt: "Sample entry",
		publishedAt: "2026-05-15T12:00:00.000Z",
		readTimeMinutes: 4,
		authorName: "Sam Rivera",
		authorInitials: "SR",
		authorImageUrl: null,
		category: null,
	},
];

/** Coerce to a positive integer; invalid/empty → default. No upper cap — author controls count. */
export function clampEntriesGridLimit(raw: unknown): number {
	const n =
		typeof raw === "number" ? raw : Number.parseInt(String(raw ?? ""), 10);
	if (!Number.isFinite(n) || n < 1) {
		return ENTRIES_GRID_LIMIT_DEFAULT;
	}
	return Math.floor(n);
}

export function normalizeEntriesGridColumns(raw: unknown): EntriesGridColumns {
	const n = typeof raw === "number" ? raw : Number(raw);
	if (n === 1 || n === 2 || n === 4) {
		return n;
	}
	return 3;
}

export function normalizeEntriesGridVariant(
	raw: unknown,
	legacyLayout?: unknown
): EntriesGridVariant {
	if (
		raw === "grid" ||
		raw === "list" ||
		raw === "featured_grid" ||
		raw === "masonry"
	) {
		return raw;
	}
	if (legacyLayout === "list") {
		return "list";
	}
	return "grid";
}

export function normalizeEntriesGridCardStyle(
	raw: unknown
): EntriesGridCardStyle {
	return raw === "overlay" ? "overlay" : "stacked";
}

export function normalizeEntriesGridDensity(raw: unknown): EntriesGridDensity {
	if (raw === "compact" || raw === "spacious") {
		return raw;
	}
	return "comfortable";
}

export function normalizeEntriesGridImageAspect(
	raw: unknown
): EntriesGridImageAspect {
	if (
		raw === "16/9" ||
		raw === "4/3" ||
		raw === "3/2" ||
		raw === "1/1" ||
		raw === "16/10"
	) {
		return raw;
	}
	return "16/10";
}

export function normalizeEntriesGridCtaStyle(
	raw: unknown
): EntriesGridCtaStyle {
	if (raw === "text_link" || raw === "button") {
		return raw;
	}
	return "none";
}

export function gapForEntriesGridDensity(
	density: EntriesGridDensity,
	override?: string
): string {
	const trimmed = override?.trim();
	if (trimmed) {
		return trimmed;
	}
	if (density === "compact") {
		return "0.75rem";
	}
	if (density === "spacious") {
		return "2rem";
	}
	return "1.25rem";
}

function cleanResponsiveBucket(
	bucket: EntriesGridResponsiveBucket | undefined
): EntriesGridResponsiveBucket | undefined {
	if (!bucket || typeof bucket !== "object") {
		return undefined;
	}
	const out: EntriesGridResponsiveBucket = {};
	if (bucket.columns != null) {
		out.columns = normalizeEntriesGridColumns(bucket.columns);
	}
	if (bucket.variant) {
		out.variant = normalizeEntriesGridVariant(bucket.variant);
	}
	if (bucket.cardStyle) {
		out.cardStyle = normalizeEntriesGridCardStyle(bucket.cardStyle);
	}
	if (bucket.density) {
		out.density = normalizeEntriesGridDensity(bucket.density);
	}
	if (typeof bucket.gap === "string" && bucket.gap.trim()) {
		out.gap = bucket.gap.trim();
	}
	if (bucket.imageAspect) {
		out.imageAspect = normalizeEntriesGridImageAspect(bucket.imageAspect);
	}
	if (typeof bucket.titleFontSize === "string" && bucket.titleFontSize.trim()) {
		out.titleFontSize = bucket.titleFontSize.trim();
	}
	if (typeof bucket.showImage === "boolean") {
		out.showImage = bucket.showImage;
	}
	if (typeof bucket.showExcerpt === "boolean") {
		out.showExcerpt = bucket.showExcerpt;
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

export function normalizeEntriesGridResponsiveLayout(
	raw: unknown
): EntriesGridResponsiveLayout | undefined {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		return undefined;
	}
	const rec = raw as EntriesGridResponsiveLayout;
	const tablet = cleanResponsiveBucket(rec.tablet);
	const mobile = cleanResponsiveBucket(rec.mobile);
	if (!(tablet || mobile)) {
		return undefined;
	}
	return {
		...(tablet ? { tablet } : {}),
		...(mobile ? { mobile } : {}),
	};
}

export function entriesGridConfigFromElement(
	el: Pick<
		EntriesGridElement,
		| "mode"
		| "postTypeId"
		| "entryIds"
		| "limit"
		| "sortBy"
		| "sortDir"
		| "columns"
		| "layout"
		| "variant"
		| "taxonomyTermId"
		| "excludeCurrentEntry"
	>
): EntriesGridResolveRequest {
	return {
		mode: el.mode === "static" ? "static" : "dynamic",
		postTypeId: el.postTypeId?.trim() || undefined,
		entryIds: Array.isArray(el.entryIds)
			? el.entryIds.filter((id) => typeof id === "string" && id.trim())
			: [],
		limit: clampEntriesGridLimit(el.limit),
		sortBy: el.sortBy ?? "published_at",
		sortDir: el.sortDir === "asc" ? "asc" : "desc",
		columns: normalizeEntriesGridColumns(el.columns),
		variant: normalizeEntriesGridVariant(el.variant, el.layout),
		taxonomyTermId: el.taxonomyTermId?.trim() || undefined,
		excludeCurrentEntry: el.excludeCurrentEntry !== false,
	};
}

export function parseEntriesGridItemsJson(
	raw: unknown
): EntriesGridResolvedItem[] | null {
	if (typeof raw !== "string" || !raw.trim()) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) {
			return null;
		}
		return parsed.filter(
			(row): row is EntriesGridResolvedItem =>
				Boolean(row) &&
				typeof row === "object" &&
				typeof (row as { id?: unknown }).id === "string" &&
				typeof (row as { title?: unknown }).title === "string" &&
				typeof (row as { href?: unknown }).href === "string"
		);
	} catch {
		return null;
	}
}

export type EntriesGridRenderConfig = Pick<
	EntriesGridElement,
	| "mode"
	| "postTypeId"
	| "postTypeName"
	| "entryIds"
	| "limit"
	| "sortBy"
	| "sortDir"
	| "columns"
	| "layout"
	| "variant"
	| "cardStyle"
	| "density"
	| "imageAspect"
	| "gap"
	| "ctaStyle"
	| "ctaLabel"
	| "showTitle"
	| "showImage"
	| "showExcerpt"
	| "showDate"
	| "showAuthor"
	| "showTaxonomy"
	| "showReadTime"
	| "titleFontSize"
	| "truncateTitle"
	| "titleMaxChars"
	| "truncateExcerpt"
	| "excerptMaxChars"
	| "taxonomyTermId"
	| "excludeCurrentEntry"
	| "responsiveLayout"
>;

export function parseEntriesGridConfigJson(
	raw: unknown
): Partial<EntriesGridRenderConfig> {
	if (typeof raw !== "string" || !raw.trim()) {
		return {};
	}
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			return parsed as Partial<EntriesGridRenderConfig>;
		}
	} catch {
		/* use defaults */
	}
	return {};
}

export type EntriesGridEffectivePresentation = {
	cardStyle: EntriesGridCardStyle;
	columns: EntriesGridColumns;
	ctaLabel: string;
	ctaStyle: EntriesGridCtaStyle;
	density: EntriesGridDensity;
	excerptMaxChars: number;
	gap: string;
	imageAspect: EntriesGridImageAspect;
	showAuthor: boolean;
	showDate: boolean;
	showExcerpt: boolean;
	showImage: boolean;
	showReadTime: boolean;
	showTaxonomy: boolean;
	showTitle: boolean;
	/** Resolved title size; empty string → CSS density defaults. */
	titleFontSize: string;
	titleMaxChars: number;
	truncateExcerpt: boolean;
	truncateTitle: boolean;
	variant: EntriesGridVariant;
};

export function effectiveEntriesGridPresentation(
	config: Partial<EntriesGridRenderConfig>,
	viewport: "desktop" | "tablet" | "mobile" = "desktop"
): EntriesGridEffectivePresentation {
	const baseVariant = normalizeEntriesGridVariant(
		config.variant,
		config.layout
	);
	const base: EntriesGridEffectivePresentation = {
		variant: baseVariant,
		columns: normalizeEntriesGridColumns(config.columns),
		cardStyle: normalizeEntriesGridCardStyle(config.cardStyle),
		density: normalizeEntriesGridDensity(config.density),
		imageAspect: normalizeEntriesGridImageAspect(config.imageAspect),
		gap: gapForEntriesGridDensity(
			normalizeEntriesGridDensity(config.density),
			config.gap
		),
		ctaStyle: normalizeEntriesGridCtaStyle(config.ctaStyle),
		ctaLabel: config.ctaLabel?.trim() || "Read more",
		showTitle: config.showTitle !== false,
		showImage: config.showImage !== false,
		showExcerpt: config.showExcerpt !== false,
		showDate: config.showDate !== false,
		showAuthor: config.showAuthor === true,
		showTaxonomy: config.showTaxonomy === true,
		showReadTime: config.showReadTime === true,
		titleFontSize:
			typeof config.titleFontSize === "string"
				? config.titleFontSize.trim()
				: "",
		truncateTitle: config.truncateTitle === true,
		titleMaxChars: clampEntriesGridMaxChars(
			config.titleMaxChars,
			ENTRIES_GRID_TITLE_MAX_CHARS_DEFAULT
		),
		truncateExcerpt: config.truncateExcerpt === true,
		excerptMaxChars: clampEntriesGridMaxChars(
			config.excerptMaxChars,
			ENTRIES_GRID_EXCERPT_MAX_CHARS_DEFAULT
		),
	};

	const ro = config.responsiveLayout;
	const bucket =
		viewport === "mobile"
			? (ro?.mobile ?? ro?.tablet)
			: viewport === "tablet"
				? ro?.tablet
				: undefined;
	if (!bucket) {
		return base;
	}

	const density = bucket.density
		? normalizeEntriesGridDensity(bucket.density)
		: base.density;
	return {
		...base,
		variant: bucket.variant
			? normalizeEntriesGridVariant(bucket.variant)
			: base.variant,
		columns:
			bucket.columns == null
				? base.columns
				: normalizeEntriesGridColumns(bucket.columns),
		cardStyle: bucket.cardStyle
			? normalizeEntriesGridCardStyle(bucket.cardStyle)
			: base.cardStyle,
		density,
		imageAspect: bucket.imageAspect
			? normalizeEntriesGridImageAspect(bucket.imageAspect)
			: base.imageAspect,
		gap: gapForEntriesGridDensity(density, bucket.gap ?? config.gap),
		showImage:
			typeof bucket.showImage === "boolean" ? bucket.showImage : base.showImage,
		showExcerpt:
			typeof bucket.showExcerpt === "boolean"
				? bucket.showExcerpt
				: base.showExcerpt,
		titleFontSize:
			typeof bucket.titleFontSize === "string" && bucket.titleFontSize.trim()
				? bucket.titleFontSize.trim()
				: base.titleFontSize,
	};
}

export function placeholderItemsForEntriesGrid(
	limit: number
): EntriesGridResolvedItem[] {
	const count = clampEntriesGridLimit(limit);
	const out: EntriesGridResolvedItem[] = [];
	for (let i = 0; i < count; i++) {
		const base =
			ENTRIES_GRID_EDITOR_PLACEHOLDERS[
				i % ENTRIES_GRID_EDITOR_PLACEHOLDERS.length
			]!;
		out.push({
			...base,
			id: `${base.id}-${i}`,
			title: i < 3 ? base.title : `Sample entry ${i + 1}`,
		});
	}
	return out;
}
